<script>
import { computed, defineComponent, ref } from 'vue'
import { useCustomization } from '@/hooks/useCustomization'

export default defineComponent({
  name: 'GuideCarousel',
  emits: ['done'],
  setup(_, ctx) {
    const guideSlide = ref(1)
    const guideCarouselRef = ref()

    const customization = useCustomization()

    const isDemoMode = computed(() => customization.getCurrentDemoMode.value)
    const isFinal = computed(() => guideSlide.value === (isDemoMode.value ? 4 : 6))

    const handleNextGuide = () => {
      if (isFinal.value)
        ctx.emit('done')
      else
        guideCarouselRef.value.next()
    }

    return {
      isFinal,
      isDemoMode,
      guideSlide,
      guideCarouselRef,
      handleNextGuide,
    }
  },
})
</script>

<template>
  <q-card>
    <q-carousel
      ref="guideCarouselRef"
      v-model="guideSlide"
      animated
      navigation
      transition-prev="slide-right"
      transition-next="slide-left"
      class="full-height"
    >
      <q-carousel-slide :name="1">
        <q-card-section class="q-px-sm q-pt-none" style="min-height: 450px;">
          <q-img
            src="@/assets/img/guide_slide_1.png"
            style="height: 190px;"
            fit="contain"
          />
          <div class="text-body2">
            ハロマイのバーチャルフィッティングは、お手持ちのスマホのカメラ機能を使用します。
          </div>
          <div class="text-body2 q-my-xs">
            測定が開始されると、カメラへのアクセス権限を求める案内が表示されますので、「許可」や「OK」をご選択ください。
          </div>
          <div class="text-small">
            <span class="text-red">※</span> カメラ機能を使用して測定を行いますが、撮影や写真データの取得を行うものではありません。
          </div>
          <div style="height: 100px;" />
        </q-card-section>
      </q-carousel-slide>
      <q-carousel-slide :name="2">
        <q-card-section class="q-px-sm q-pt-none" style="min-height: 450px;">
          <q-img
            src="@/assets/img/guide_slide_2.png"
            style="height: 190px;"
            fit="contain"
          />
          <div class="text-body2">
            画面の下に並ぶアイコンから、アイウェアのカスタマイズができます。
          </div>
          <div class="text-body2 q-my-xs">
            モデルやフレームの色、チタンカラーやレンズの種類まで自由自在！
          </div>
          <div class="text-body2">
            お好みのアイウェアを作ってみましょう。
          </div>
          <div style="height: 100px;" />
        </q-card-section>
      </q-carousel-slide>
      <q-carousel-slide :name="3">
        <q-card-section class="q-px-sm q-pt-none" style="min-height: 450px;">
          <q-img
            src="@/assets/img/guide_slide_3.png"
            style="height: 190px;"
            fit="contain"
          />
          <div class="text-body2 q-mb-md">
            画面の上にあるボタンで、バーチャルフィッティングのモードを切り替えることができます。
          </div>
          <div class="text-body2 q-my-xs">
            試着 … 顔に合わせてバーチャル試着
          </div>
          <div class="text-body2">
            360°ビュー … 様々な方向から仕上がり確認
          </div>
          <div style="height: 100px;" />
        </q-card-section>
      </q-carousel-slide>
      <template v-if="isDemoMode">
        <q-carousel-slide :name="4">
          <q-card-section class="q-px-sm q-pt-none" style="min-height: 450px;">
            <q-img
              src="@/assets/img/guide_slide_7.png"
              style="height: 142px;"
              fit="contain"
              class="q-my-lg"
            />
            <div class="text-body2">
              ご希望のアイウェアが完成しましたら、「このデザインでLINE購入」ボタンを押して、LINE公式アカウントを友達追加したのちに自動生成されるトーク文をそのままご送信ください。
            </div>
            <div class="text-body2">
              ※オペレーターよりアイウェア購入のための詳細ご案内をさせていただきます。
            </div>
            <div class="text-body2">
              ※各種ご質問についてもLINE上にて承ります。
            </div>
            <div style="height: 100px;" />
          </q-card-section>
        </q-carousel-slide>
      </template>
      <template v-else>
        <q-carousel-slide :name="4">
          <q-card-section class="q-px-sm q-pt-none" style="min-height: 450px;">
            <q-img
              src="@/assets/img/guide_slide_4.png"
              style="height: 190px;"
              fit="contain"
            />
            <div class="text-body2">
              出来上がったアイウェアは、「カートに保存」ボタンからカートに入れて保存しましょう。
            </div>
            <div class="text-body2 q-my-xs">
              ご購入の際は「カートを見る」ボタンより、マイページ内のカートをご確認いただき、お好みの1本を選んでご購入ください。
            </div>
            <div class="text-body2">
              ※カートのご利用にはログインが必要です。
            </div>
            <div style="height: 100px;" />
          </q-card-section>
        </q-carousel-slide>
        <q-carousel-slide :name="5">
          <q-card-section class="q-px-sm q-pt-none" style="min-height: 450px;">
            <q-img
              src="@/assets/img/guide_slide_5.png"
              style="height: 190px;"
              fit="contain"
            />
            <div class="text-body2">
              「ページを見る」ボタンから、ハロマイのウェブサイト各ページへ移動できます。
            </div>
            <div class="text-body2 q-my-xs">
              レンズの仕様やモデルのイメージ写真など、カスタマイズの参考にご確認ください。
            </div>
            <div class="text-body2">
              ※ウェブサイトTOPもこちらからどうぞ。
            </div>
            <div style="height: 100px;" />
          </q-card-section>
        </q-carousel-slide>
        <q-carousel-slide :name="6">
          <q-card-section class="q-px-sm q-pt-none" style="min-height: 450px;">
            <q-img
              src="@/assets/img/guide_slide_6.png"
              style="height: 190px;"
              fit="contain"
            />
            <div class="text-body2">
              最後に、バーチャルフィッティングでお困りのことがございましたら、チャット対応でお答えいたします。
            </div>
            <div class="text-body2 q-my-xs">
              どうぞお気軽にお問い合わせください。
            </div>
            <div class="text-body2">
              ※チャットボットでの対応となります。
            </div>
            <div style="height: 100px;" />
          </q-card-section>
        </q-carousel-slide>
      </template>
      <template #control>
        <q-carousel-control
          position="bottom"
          :offset="[0, 64]"
          class="flex flex-center"
        >
          <q-btn
            ref="refAddToCart"
            color="primary"
            rounded
            glasson-click="menu_cart_button"
            align="between"
            icon=""
            push
            padding="12px 16px"
            class="text-weight-thin"
            style="width: 250px"
            @click="handleNextGuide"
          >
            <span class="text-body2">{{ isFinal ? '始める' : '次へ' }}</span>
            <svg width="14" height="14" viewBox="0 0 6.382 11.921">
              <path
                id="パス_172"
                data-name="パス 172"
                d="M784.36,5034.028l5.365,5.365-5.365,5.365"
                transform="translate(-783.764 -5033.433)"
                fill="#000"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="0.843"
              />
            </svg>
          </q-btn>
        </q-carousel-control>
      </template>
      <template #navigation-icon="{ active, onClick }">
        <q-btn v-if="active" size="xs" padding="sm" style="margin: 6px;" color="primary" round dense @click="onClick" />
        <q-btn v-else size="xs" color="primary" padding="sm" style="margin: 6px;" outline round dense @click="onClick" />
      </template>
    </q-carousel>
  </q-card>
</template>
